import {
  Button,
  Checkbox,
  Drawer,
  DrawerProps,
  Edit,
  Form,
  FormProps,
  Input,
  InputNumber,
  Radio,
  Space,
  Typography
} from '@pankod/refine-antd'
import { useNotification, useTranslate } from '@pankod/refine-core'
import { definitions } from 'interfaces'
import { useEffect, useState } from 'react'
import { ADMITTED_STATUS, API_URL, DEFAULT_PENALIZATION_AMOUNT, ECONOMY_SHIPMENT_METHOD_ID, ORDER_STATUS_ID_SUBSCRIPTION, PARCIAL_RETURN_STATUS, PENALIZATION_STATUS, REFUND_TYPE, RETURN_STATUS, supabaseClient } from 'utility'
import { processNewStatus } from 'utility/orderPostActions'

type refundDrawerProps = {
  drawerProps: DrawerProps
  formProps: FormProps
  close: () => void
  order: definitions['orders']
  isPenalization: boolean
  isCorrection: boolean
  isRefund: boolean
}

export const RefundDrawer: React.FC<refundDrawerProps> = ({
  drawerProps,
  formProps,
  close,
  order,
  isPenalization = false,
  isCorrection,
  isRefund
}) => {
  const t = useTranslate()
  const { open } = useNotification()
  const Action = Edit

  const [description, setDescription] = useState('')
  const [amount, setAmount] = useState(0)
  const [refundToggle, setRefundToggle] = useState<string | undefined>(undefined)
  const [completeToggle, setCompleteToggle] = useState<string | undefined>(undefined)
  const [itemRefundList, setItemRefundList] = useState<number[]>([])
  const [itemIgicList, setItemIgicList] = useState<number[]>([])
  const [itemAiemList, setItemAiemList] = useState<number[]>([])
  const [correctionLines, setCorrectionLines] = useState<{ description: string; amount: number }[]>([{ description: '', amount: 0 }])
  const [supplement, setSupplement] = useState<number>(DEFAULT_PENALIZATION_AMOUNT)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (order?.status_id === ORDER_STATUS_ID_SUBSCRIPTION) {
      setRefundToggle('all')
      setCompleteToggle('complete')
    }
  }, [order?.status_id])

  const successNotification = () => open?.({
    type: 'success',
    message: t('notifications.successMsg')
  })

  const errorNotification = () => open?.({
    type: 'error',
    message: t('notifications.errorMsg')
  })

  const addRemoveToList = (checked: boolean, id: number) => {
    if (checked) setItemRefundList([...itemRefundList, id])
    else setItemRefundList([...itemRefundList.filter((idList: number) => idList !== id)])
  }

  const addRemoveIgicToList = (checked: boolean, id: number) => {
    if (checked) setItemIgicList([...itemIgicList, id])
    else setItemIgicList([...itemIgicList.filter((idList: number) => idList !== id)])
  }

  const addRemoveAiemToList = (checked: boolean, id: number) => {
    if (checked) setItemAiemList([...itemAiemList, id])
    else setItemAiemList([...itemAiemList.filter((idList: number) => idList !== id)])
  }

  const resetDrawerState = () => {
    setDescription('')
    setAmount(0)
    setRefundToggle(undefined)
    setCompleteToggle(undefined)
    setItemRefundList([])
    setItemIgicList([])
    setItemAiemList([])
    setCorrectionLines([{ description: '', amount: 0 }])
    setSupplement(DEFAULT_PENALIZATION_AMOUNT)
  }

  const handleOnClose = () => {
    resetDrawerState()
    close()
  }

  const handlePenalization = async () => {
    setLoading(true)
    await processNewStatus(
      PENALIZATION_STATUS,
      { ...order!, user: (order as any).user },
      supplement
    )
    setLoading(false)
    resetDrawerState()
    close()
  }

  const insertRefund = async (orderId: number, desc: string, amountInsert: number, isSimpleRefund = true) => {
    const { data: refunds } = await supabaseClient
      .from('refunds')
      .select('*')
      .eq('order_id', order.id)
      .eq('deleted', false)
      .eq('type', 'client')
      .eq('status_id', ADMITTED_STATUS)

    const admittedRefunds = refunds?.reduce((sum, r) => sum + r.amount, 0)

    const { data, error } = await supabaseClient.from('refunds')
      .insert({
        order_id: orderId,
        description: desc,
        amount: amountInsert,
        type: REFUND_TYPE.client,
        updated_by: supabaseClient.auth.user()?.email,
        refund_supplements: isSimpleRefund ? 0 : admittedRefunds
      })
      .eq('order_id', orderId)

    if (error) {
      console.error('Error inserting refund:', error)
      errorNotification()
    }

    return data?.[0]?.id
  }

  const insertCorrection = async (orderId: number, desc: string, amountInsert: number) => {
    const { data, error } = await supabaseClient.from('corrections')
      .insert({
        order_id: orderId,
        description: desc,
        amount: amountInsert,
        type: REFUND_TYPE.client,
        updated_by: supabaseClient.auth.user()?.email
      })
      .eq('order_id', orderId)

    if (error) {
      console.error('Error inserting correction:', error)
      errorNotification()
    }

    return data?.[0]?.id
  }

  const handleCorrectionLineChange = (index: number, field: 'description' | 'amount', value: string | number) => {
    setCorrectionLines(prev => prev.map((line, lineIndex) => lineIndex === index ? { ...line, [field]: value } : line))
  }

  const addCorrectionLine = () => {
    setCorrectionLines(prev => [...prev, { description: '', amount: 0 }])
  }

  const updateOrderStatus = async (newStatusId: number) => {
    await supabaseClient.from('orders').update({
      prev_status_id: order.status_id,
      status_id: newStatusId,
      updated_by: supabaseClient.auth.user()?.email
    }).eq('id', order.id)
  }

  const updateItemsAsReturned = async (itemsId: number[]) => {
    await supabaseClient.from('items')
      .update({ returned: true })
      .in('id', itemsId)
  }

  return (
    <Drawer
      {...drawerProps}
      onClose={handleOnClose}
      width={'600px'}
      bodyStyle={{ padding: 0 }}
      zIndex={1001}
    >
      {(!isPenalization) && <Action
        isLoading={loading}
        saveButtonProps={{
          onClick: async () => {
            setLoading(true)

            let currentRefundIds: number[] = []

            if (refundToggle === 'other') {
              const id = await insertRefund(order.id, description, amount)
              if (id) currentRefundIds.push(id)
              // Set order status to the newStatusId
              await updateOrderStatus(PARCIAL_RETURN_STATUS)
            } else if (refundToggle === 'all') {
              if (completeToggle === 'complete') {
                await processNewStatus(RETURN_STATUS, { ...order, user: (order as any).user })
              } else if (completeToggle === 'items') {
                await insertRefund(order.id, 'Artículos', order.syniva_price, false)
                // Set order status to the newStatusId
                await updateOrderStatus(RETURN_STATUS)
              }
              await updateItemsAsReturned((order as any).items.map((item: definitions['items']) => item.id))
            } else if (refundToggle === 'partial') {
              if (itemRefundList.length > 0 || itemIgicList.length > 0 || itemAiemList.length > 0) {
                const itemsToRefund = (order as any).items.filter((item: definitions['items']) => itemRefundList.includes(item.id))
                const itemsForIgic = (order as any).items.filter((item: definitions['items']) => itemIgicList.includes(item.id))
                const itemsForAiem = (order as any).items.filter((item: definitions['items']) => itemAiemList.includes(item.id))

                const promises: Promise<any>[] = []

                // Insert refunds for selected items
                itemsToRefund.forEach((item: definitions['items']) => {
                  promises.push(insertRefund(order.id, item.name, order.shipping_method_id === ECONOMY_SHIPMENT_METHOD_ID ? item.price : item.syniva_price!))
                })

                // Insert refunds for selected IGIC items
                itemsForIgic.forEach((item: definitions['items']) => {
                  if (item.igic_price && item.igic_price > 0) {
                    promises.push(insertRefund(order.id, `${item.name} - IGIC`, item.igic_price))
                  }
                })

                // Insert refunds for selected AIEM items
                itemsForAiem.forEach((item: definitions['items']) => {
                  if (item.tariff_price && item.tariff_price > 0) {
                    promises.push(insertRefund(order.id, `${item.name} - AIEM`, item.tariff_price))
                  }
                })

                const returnedIds = await Promise.all(promises)
                currentRefundIds = [...currentRefundIds, ...returnedIds.filter(id => id)]

                await updateItemsAsReturned(itemsToRefund.map((item: definitions['items']) => item.id))

                if (itemRefundList.includes(0) && amount > 0) {
                  const idOther = await insertRefund(order.id, t('refund.fields.other'), amount)
                  if (idOther) currentRefundIds.push(idOther)
                }
                await updateOrderStatus(PARCIAL_RETURN_STATUS)
              }
            } else if (isCorrection) {
              // control the format, if description is empty or amount is 0, show error and do not insert
              const validCorrections = correctionLines.filter(line => line.description.trim() !== '' && line.amount > 0)

              if (validCorrections.length === 0) {
                errorNotification()
                setLoading(false)
                return
              }

              const returnedIds = await Promise.all(validCorrections.map(line => insertCorrection(order.id, line.description, line.amount)))

              currentRefundIds = [...currentRefundIds, ...returnedIds.filter(id => id)]
            } else if (isRefund) {
              const validRefunds = correctionLines.filter(line => line.description.trim() !== '' && line.amount > 0)

              if (validRefunds.length === 0) {
                errorNotification()
                setLoading(false)
                return
              }

              const promises: Promise<any>[] = []

              validRefunds.forEach(line => {
                promises.push(insertRefund(order.id, line.description, line.amount))
              })
              const returnedIds = await Promise.all(promises)
              currentRefundIds = [...currentRefundIds, ...returnedIds.filter(id => id)]
            }
            successNotification()
            setLoading(false)

            let url = `${API_URL}/refunds/pdf/${order.id}`
            const params = new URLSearchParams()

            if (refundToggle !== 'all') {
              params.append('simple', 'true')
            }
            if (currentRefundIds.length > 0) {
              params.append('refundIds', currentRefundIds.join(','))
            }
            if (isCorrection) {
              params.append('isCorrection', 'true')
            }

            const queryString = params.toString()
            if (queryString) {
              url += `?${queryString}`
            }

            window.open(url, '_blank', 'noopener,noreferrer')
            window.location.reload()
          }
        }}
        title={isCorrection ? t('actions.refundDrawer.correctionTitle') : isRefund ? t('actions.refundDrawer.refundTitle') : t('actions.refundDrawer.title')}
        breadcrumb={false}
        goBack={false}
        headerProps={{ extra: null }}
      >
        <Form {...formProps} layout="vertical">
          <Form.Item
            hidden={true}
            name="order_id"
          >
            <Input />
          </Form.Item>

          {(!isCorrection && !isRefund && order?.status_id !== ORDER_STATUS_ID_SUBSCRIPTION)
            ? (<Form.Item>
            <Radio.Group
              style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}
              onChange={(event) => setRefundToggle(event.target.value)}
              value={refundToggle}
            >
              <Radio value={'all'}>{t('refund.fields.allOrder')}</Radio>
                <>
                  <Radio value={'partial'}>{t('refund.fields.partialOrder')}</Radio>
                  <Radio value={'other'}>{t('refund.fields.other')}</Radio>
                </>
            </Radio.Group>
          </Form.Item>)
            : (!isCorrection && !isRefund && order?.status_id === ORDER_STATUS_ID_SUBSCRIPTION) && (
              <Form.Item>
                <Radio.Group
                  style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}
                  onChange={(event) => setRefundToggle(event.target.value)}
                  value={refundToggle}
                >
                  <Radio value={'all'} disabled>{t('refund.fields.allOrder')}</Radio>
                </Radio.Group>
              </Form.Item>
              )
          }

          {(isCorrection || isRefund) && (
            <div style={{ display: 'grid', gap: '1rem' }}>
              {correctionLines.map((line, index) => (
                <div key={`correction-line-${index}`} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
                  <Form.Item
                    label={t('refund.fields.description')}
                    required
                    style={{ flex: 1, marginBottom: 0 }}
                  >
                    <Input
                      value={line.description}
                      onChange={(event) => handleCorrectionLineChange(index, 'description', event.target.value)}
                    />
                  </Form.Item>
                  <Form.Item
                    label={t('refund.fields.amount')}
                    required
                    style={{ width: '180px', marginBottom: 0 }}
                  >
                    <InputNumber
                      value={line.amount}
                      min={0}
                      onChange={(value) => handleCorrectionLineChange(index, 'amount', value ?? 0)}
                      decimalSeparator=','
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </div>
              ))}
              <Button type='dashed' onClick={addCorrectionLine} style={{ width: '50%', margin: '0 auto' }}>
                {t('refund.fields.addLine')}
              </Button>
            </div>
          )}

          {(refundToggle === 'all' && order?.status_id !== ORDER_STATUS_ID_SUBSCRIPTION)
            ? <Form.Item>
              <Radio.Group
                style={{ display: 'flex', justifyContent: 'left', gap: '3rem' }}
                onChange={(event) => setCompleteToggle(event.target.value)}
              >
                <Radio value={'complete'}>{t('refund.fields.complete')}</Radio>
                <Radio value={'items'}>{t('refund.fields.items')}</Radio>
              </Radio.Group>
            </Form.Item>
            : null}

          {(refundToggle === 'partial')
            ? <div style={{ display: 'grid' }}>
              {(order as any).items?.map((item: definitions['items']) => {
                if (!item.disabled && !item.returned) {
                  return (
                    <div key={`item-container-${item.id}`} style={{ marginBottom: '0.5rem' }}>
                      <Checkbox
                        style={{ margin: '0' }}
                        id={`item-${item.id}`}
                        name={item.name}
                        value={item.id}
                        onChange={(props) => addRemoveToList(props.target.checked, item.id)}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '35em' }}>
                          <b>{`${item.name.slice(0, 50)}`}</b>
                          <p style={{ marginRight: '2rem' }}>({order.shipping_method_id === ECONOMY_SHIPMENT_METHOD_ID ? item.price : item.syniva_price}€)</p>
                        </div>
                      </Checkbox>
                      <div style={{ marginLeft: '1.5rem', marginTop: '0.25rem', display: 'flex', gap: '1.5rem' }}>
                        {item.igic_price > 0 && (
                          <Checkbox
                            style={{ margin: '0' }}
                            id={`item-igic-${item.id}`}
                            name={`${item.name} - ${t('common.igic')}`}
                            value={`igic-${item.id}`}
                            onChange={(props) => addRemoveIgicToList(props.target.checked, item.id)}
                          >
                            <span style={{ fontSize: '0.9em', color: '#666' }}>
                              {`${t('common.igic')} ${item.igic_price}€`}
                            </span>
                          </Checkbox>
                        )}
                        {item.tariff_price > 0 && (
                          <Checkbox
                            style={{ margin: '0' }}
                            id={`item-aiem-${item.id}`}
                            name={`${item.name} - ${t('common.aiem')}`}
                            value={`aiem-${item.id}`}
                            onChange={(props) => addRemoveAiemToList(props.target.checked, item.id)}
                          >
                            <span style={{ fontSize: '0.9em', color: '#666' }}>
                              {`${t('common.aiem')} ${item.tariff_price}€`}
                            </span>
                          </Checkbox>
                        )}
                      </div>
                    </div>
                  )
                } else return null
              })}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1rem', width: '80%' }}>
                <Checkbox
                  style={{ margin: '0', width: '70%' }}
                  key={'item-other'}
                  id={'item-other'}
                  name={'other'}
                  value={'other'}
                  onChange={(props) => addRemoveToList(props.target.checked, 0)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', width: '35em' }}>
                    <b>{t('refund.fields.other')}</b>
                  </div>
                </Checkbox>
                <InputNumber value={amount} min={0} onChange={(value) => setAmount(value ?? 0)} decimalSeparator=',' />
              </div>
            </div>
            : null
          }

          {(refundToggle === 'other')
            ? <div>
              <Form.Item
                label={t('refund.fields.description')}
                getValueProps={(value) => value}
                required
              >
                <Input value={description} onChange={(event) => setDescription(event.target?.value)} />
              </Form.Item>
              <Form.Item
                label={t('refund.fields.amount')}
                name="amount"
                getValueProps={(value) => value}
              >
                <InputNumber value={amount} min={0} onChange={(value) => setAmount(value ?? 0)} decimalSeparator=',' />
              </Form.Item>
            </div>
            : null
          }

        </Form>
      </Action>}

      {(isPenalization) && <Space style={{ marginLeft: '2rem', marginTop: '2rem', gap: '2rem' }} direction='horizontal' align='center'>
        <Typography.Text>{t('supplement.fields.supplement')}</Typography.Text>
        <InputNumber
          value={supplement}
          onChange={(value) => setSupplement(value)}
          min={0}
          decimalSeparator=','
        />
        <Button
          type='primary'
          onClick={async () => {
            await handlePenalization()
            window.open(`${API_URL}/billings/pdf/${order.id}`, '_blank', 'noopener,noreferrer')
          }}
          disabled={loading}
        >
          {t('order.actions.createPenalization')}
        </Button>
      </Space>}
    </Drawer>
  )
}
